import {ClaimMissionRewardResponse_SuccessResult} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coins/v1/service.types';
import {
  CONTENT_POPPER_ANIMATION_DURATION_MOUNT,
  CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT,
  useCallbackDelayed,
} from '@aboutyou/ui-components';
import {startTransition, useCallback, useEffect, useState} from 'react';

export const REWARD_TOTAL_COUNTUP_DELAY_IN_MS = 900;
const TIME_DELAY_TO_FADE_IN_BODY_CONTENT = 100;
const SHOW_CONTENT_DELAY_TIME_IN_MS =
  CONTENT_POPPER_ANIMATION_DURATION_MOUNT + TIME_DELAY_TO_FADE_IN_BODY_CONTENT;

interface UseCoinsClaimSuccessPopupReturn {
  showBodyContent: boolean;
  animatedTotalCoins: string | undefined;
}

export const useCoinsClaimSuccessPopup = ({
  coinsData,
  isOpen,
}: {
  isOpen: boolean;
  coinsData: ClaimMissionRewardResponse_SuccessResult;
}): UseCoinsClaimSuccessPopupReturn => {
  const [showBodyContent, setShowBodyContent] = useState(false);
  const preClaimRedeemable = coinsData?.benefit?.preClaimRedeemable;
  const postClaimRedeemable = coinsData?.benefit?.postClaimRedeemable;
  const [animatedTotalCoins, setAnimatedTotalCoins] = useState<
    string | undefined
  >(preClaimRedeemable);

  const {delayedCallback: delayedUpdateAnimatedTotalCoins} = useCallbackDelayed(
    () => {
      setAnimatedTotalCoins(postClaimRedeemable);
    },
    REWARD_TOTAL_COUNTUP_DELAY_IN_MS,
  );

  const {delayedCallback: delayedContentReveal} = useCallbackDelayed(() => {
    setShowBodyContent(true);
    delayedUpdateAnimatedTotalCoins();
  }, SHOW_CONTENT_DELAY_TIME_IN_MS);

  const {delayedCallback: delayedResetAnimation} = useCallbackDelayed(() => {
    setAnimatedTotalCoins(undefined);
    setShowBodyContent(false);
  }, CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT);

  const initializeAnimation = useCallback(() => {
    setAnimatedTotalCoins(preClaimRedeemable);
    setShowBodyContent(false);
    delayedContentReveal();
  }, [delayedContentReveal, preClaimRedeemable]);

  useEffect(() => {
    if (isOpen) {
      startTransition(() => {
        initializeAnimation();
      });
    } else {
      delayedResetAnimation();
    }
  }, [isOpen]);

  return {
    showBodyContent,
    animatedTotalCoins,
  };
};
